import {
  collection,
  doc,
  getDoc,
  getDocs,
  increment,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { saveDeckToFirebase } from "../game/deckEngine";
import {
  ensureInitialSignupRewards,
  ensureTutorialCompletionReward,
} from "./signupRewards";

export const LEGACY_TUTORIAL_SEQUENCES = new Set([
  "guidedBattle",
  "firstDeck",
  "howToPlay",
]);

const sanitizeCompletedSequences = (completedSequences = []) =>
  Array.from(
    new Set(
      (completedSequences || []).filter(
        (sequenceId) =>
          sequenceId && !LEGACY_TUTORIAL_SEQUENCES.has(sequenceId)
      )
    )
  );

async function autoClaimAllInitialRewards(userId) {
  const rewardsRef = collection(db, "users", userId, "rewards");
  const rewardsSnap = await getDocs(rewardsRef);
  if (rewardsSnap.empty) return;

  const unclaimedRewards = rewardsSnap.docs
    .map((rewardDoc) => ({ id: rewardDoc.id, ...rewardDoc.data() }))
    .filter((reward) => !reward.claimed);

  if (unclaimedRewards.length === 0) return;

  const [infinityCardsSnap, characterCardsSnap] = await Promise.all([
    getDocs(collection(db, "users", userId, "Infinity cards")),
    getDocs(collection(db, "users", userId, "Character cards")),
  ]);

  const existingInfinityCardIds = new Set(
    infinityCardsSnap.docs.map((cardDoc) => cardDoc.id)
  );
  const existingCharacterCardIds = new Set(
    characterCardsSnap.docs.map((cardDoc) => cardDoc.id)
  );

  let tokensToAdd = 0;
  const characterRewardMap = new Map();
  const infinityRewardMap = new Map();
  const claimedAt = Date.now();

  for (const reward of unclaimedRewards) {
    switch (reward.type) {
      case "infinity tokens": {
        tokensToAdd += reward.amount || 0;
        break;
      }

      case "character card": {
        const card = reward.reward;
        const cardId = card?.cardId || card?.id;
        if (!cardId) break;

        const existing = characterRewardMap.get(cardId);
        characterRewardMap.set(cardId, {
          card,
          amount: (existing?.amount || 0) + (reward.amount || 1),
        });
        break;
      }

      case "card pack": {
        const pack = Array.isArray(reward?.reward?.pack)
          ? reward.reward.pack
          : reward?.reward?.pack?.cards || [];

        for (const card of pack) {
          const cardId = card?.cardId || card?.id;
          if (!cardId) continue;

          const existing = infinityRewardMap.get(cardId);
          infinityRewardMap.set(cardId, {
            card,
            amount: (existing?.amount || 0) + 1,
          });
        }
        break;
      }

      default:
        break;
    }
  }

  const batch = writeBatch(db);
  const userDocRef = doc(db, "users", userId);

  if (tokensToAdd > 0) {
    batch.update(userDocRef, {
      infinityTokens: increment(tokensToAdd),
    });
  }

  for (const [cardId, entry] of characterRewardMap.entries()) {
    const cardRef = doc(db, "users", userId, "Character cards", cardId);
    if (existingCharacterCardIds.has(cardId)) {
      batch.update(cardRef, {
        amountOwned: increment(entry.amount),
        new: true,
      });
    } else {
      batch.set(cardRef, {
        ...entry.card,
        amountOwned: entry.amount,
        createdAt: claimedAt,
        lastUpdated: claimedAt,
        new: true,
      });
    }
  }

  for (const [cardId, entry] of infinityRewardMap.entries()) {
    const cardRef = doc(db, "users", userId, "Infinity cards", cardId);
    if (existingInfinityCardIds.has(cardId)) {
      batch.update(cardRef, {
        amountOwned: increment(entry.amount),
        new: true,
      });
    } else {
      batch.set(cardRef, {
        ...entry.card,
        amountOwned: entry.amount,
        createdAt: claimedAt,
        lastUpdated: claimedAt,
        new: true,
      });
    }
  }

  for (const reward of unclaimedRewards) {
    batch.update(doc(db, "users", userId, "rewards", reward.id), {
      claimed: true,
      claimedAt,
    });
  }

  await batch.commit();
}

export async function assignStarterDeckToUser(userId) {
  if (!userId) {
    return {
      success: false,
      reason: "invalid_user",
      message: "Missing user id.",
    };
  }

  const decksSnap = await getDocs(collection(db, "users", userId, "decks"));
  if (!decksSnap.empty) {
    return {
      success: false,
      reason: "has_existing_deck",
      message: "User already has at least one deck.",
    };
  }

  const ownedSnap = await getDocs(
    collection(db, "users", userId, "Infinity cards")
  );
  const ownedCards = ownedSnap.docs.map((cardDoc) => ({
    id: cardDoc.id,
    ...cardDoc.data(),
  }));

  const expandedDeck = [];
  for (const card of ownedCards) {
    const copies = Math.max(0, Number(card.amountOwned) || 0);
    for (let i = 0; i < copies; i += 1) {
      expandedDeck.push(card);
      if (expandedDeck.length === 40) break;
    }
    if (expandedDeck.length === 40) break;
  }

  if (expandedDeck.length !== 40) {
    const fallbackCards = [...expandedDeck];
    while (fallbackCards.length && expandedDeck.length < 40) {
      expandedDeck.push(
        fallbackCards[expandedDeck.length % fallbackCards.length]
      );
    }
    if (expandedDeck.length !== 40) {
      return {
        success: false,
        reason: "insufficient_inventory",
        message:
          "User does not have enough Infinity card inventory to build a starter deck.",
      };
    }
  }

  const result = await saveDeckToFirebase(
    { id: userId },
    "Starter Deck",
    "#D500F9",
    expandedDeck,
    null,
    () => {}
  );

  if (!result?.success) {
    return {
      success: false,
      reason: "error",
      message: result?.message || "Failed to assign starter deck.",
    };
  }

  return {
    success: true,
    reason: "created",
    message: "Starter Deck created successfully.",
  };
}

export async function finalizeTutorialOnboarding(
  userId,
  { completedSequences = [] } = {}
) {
  if (!userId) return;

  const userRef = doc(db, "users", userId);
  const userSnap = await getDoc(userRef);
  const existingCompleted =
    userSnap.data()?.tutorialProgress?.completedSequences || [];
  const nextCompleted = sanitizeCompletedSequences([
    ...existingCompleted,
    ...completedSequences,
  ]);

  await updateDoc(userRef, {
    completedTutorial: true,
    "tutorialProgress.currentSequence": null,
    "tutorialProgress.currentStepIndex": 0,
    "tutorialProgress.completedSequences": nextCompleted,
    "tutorialProgress.updatedAt": serverTimestamp(),
  });

  await ensureInitialSignupRewards(userId);
  await ensureTutorialCompletionReward(userId);
  await autoClaimAllInitialRewards(userId);
  await assignStarterDeckToUser(userId);
}
